import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Block from '@mui/icons-material/Block';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import Speed from '@mui/icons-material/Speed';
import TagOutlined from '@mui/icons-material/TagOutlined';
import portalAxios from '../../api/portalAxios';
import { PORTAL_COUNTERS_ENDPOINT, PORTAL_EQUIPMENT_ENDPOINT } from '../../consts';
import { usePortalAuthContext } from '../../contexts/portalAuth';
import { CLIENT_PORTAL_MODULE, PortalEquipment } from '../../types/portal';
import {
  formatCounterInputValue,
  formatCounterValue,
  formatDateTime,
  normalizeCounterInputValue,
} from '../../utils';
import { extractPortalError } from './portalError';
import { PortalSectionHeader } from './portalSectionHeader.component';
import styles from './portal.module.css';

/** Keys the entry fields by machine and counter kind. */
const fieldKey = (equipmentId: number, kind: string) => `${equipmentId}:${kind}`;

export const PortalCounters = () => {
  const { t } = useTranslation();
  const { hasModule } = usePortalAuthContext();

  const [equipment, setEquipment] = useState<PortalEquipment[] | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const canSubmit = hasModule(CLIENT_PORTAL_MODULE.COUNTER_READINGS);

  const load = useCallback(async () => {
    try {
      const response = await portalAxios.get(PORTAL_EQUIPMENT_ENDPOINT);
      setEquipment(response.data);
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    if (canSubmit) load();
  }, [canSubmit, load]);

  const handleSubmit = async () => {
    const readings = Object.entries(values)
      .filter(([, value]) => value !== '')
      .map(([key, value]) => {
        const [equipmentId, kind] = key.split(':');
        return { equipment: Number(equipmentId), kind, value };
      });

    if (readings.length === 0) return;

    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await portalAxios.post(PORTAL_COUNTERS_ENDPOINT, { readings });
      // Nothing is stored unless every row is accepted, so on success the
      // whole form is cleared and re-read to show the new "last reading".
      setValues({});
      setSaved(true);
      setEquipment(null);
      await load();
    } catch (submitError) {
      setError(extractPortalError(submitError, t('We could not save your readings.')));
    } finally {
      setSaving(false);
    }
  };

  if (!canSubmit) {
    return (
      <Box className={styles.placeholder}>
        <Block sx={{ fontSize: 40 }} />
        <Typography variant="body2">
          {t('Your service provider has not enabled this section.')}
        </Typography>
      </Box>
    );
  }

  if (loadFailed) {
    return <Alert severity="error">{t('We could not load your equipment.')}</Alert>;
  }

  if (equipment === null) {
    return (
      <Box className={styles.centered}>
        <CircularProgress />
      </Box>
    );
  }

  if (equipment.length === 0) {
    return (
      <Box className={styles.placeholder}>
        <PrintOutlined sx={{ fontSize: 40 }} />
        <Typography variant="body2">
          {t('There is no equipment assigned to your company right now.')}
        </Typography>
      </Box>
    );
  }

  const hasAnyValue = Object.values(values).some((value) => value !== '');

  return (
    <Box>
      <PortalSectionHeader
        icon={<Speed />}
        title={t('Counter readings')}
        description={t(
          'Enter the current reading shown by each machine. You can leave any of them blank.'
        )}
      />

      {saved ? (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSaved(false)}>
          {t('Thank you, your readings have been saved.')}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Box className={styles.stack}>
      {equipment.map((machine) => (
        <Paper key={machine.id} elevation={0} className={styles.cardItem}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <PrintOutlined color="primary" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {[machine.producer_name, machine.model_name].filter(Boolean).join(' ') ||
                  t('Equipment')}
              </Typography>
              <Box className={styles.metaItem}>
                <TagOutlined fontSize="small" />
                {t('Serial number')}: {machine.serial}
              </Box>
              {machine.address ? (
                <Box className={styles.metaItem}>
                  <PlaceOutlined fontSize="small" />
                  {machine.address}
                </Box>
              ) : null}
            </Box>
          </Box>

          <Box className={styles.counterInputs}>
            {machine.counters.map((counter) => (
              <TextField
                key={counter.kind}
                label={counter.kind_display}
                value={formatCounterInputValue(values[fieldKey(machine.id, counter.kind)] ?? '')}
                onChange={(event) => {
                  const key = fieldKey(machine.id, counter.kind);
                  const nextValue = event.target.value;

                  if (nextValue === '') {
                    setValues({ ...values, [key]: '' });
                    return;
                  }

                  const normalized = normalizeCounterInputValue(nextValue);
                  // null means the keystroke was not a number - ignore it
                  // rather than letting it into the field.
                  if (normalized === null) return;

                  setValues({ ...values, [key]: normalized });
                }}
                helperText={
                  counter.last_value !== null
                    ? `${t('Last reading')}: ${formatCounterValue(
                        counter.last_value
                      )} (${formatDateTime(counter.last_read_at).replace('\n', ' ')})`
                    : t('No reading yet')
                }
                inputProps={{ inputMode: 'numeric' }}
                size="small"
              />
            ))}
          </Box>
        </Paper>
      ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !hasAnyValue}>
          {saving ? t('Saving...') : t('Submit readings')}
        </Button>
      </Box>
    </Box>
  );
};
